import React, { useState } from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { ConceptLinkerValidatorItemStatus } from 'constants/conceptLinkerValidatorStatus';
import ConceptLinkerValidatorItemsModel from 'models/ConceptLinkerValidatorItemsModel';
import ModuleContext from 'CMFW/context/ModuleContext';
import { IConceptLinkerValidatorItem } from '../../../types/conceptLinkerValidator';

const ConceptValidatorActionRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as IConceptLinkerValidatorItem;
  const itemsModel = React.useContext(ModuleContext) as ConceptLinkerValidatorItemsModel;
  const [processingAction, setProcessingAction] = useState<'keep' | 'reject' | null>(null);

  if (!data) return null;

  // Don't show buttons for pending items
  if (data.status === ConceptLinkerValidatorItemStatus.PENDING) {
    return null;
  }

  // Don't show buttons for error items
  if (data.ai_result && data.ai_result.decision === 'error') {
    return (
      <ActionsContainer>
        <div style={{ color: '#ff5722', fontSize: '12px', textAlign: 'center' }}>
          <i className="fa fa-exclamation-triangle" style={{ marginRight: '4px' }} />
          Manual action required
        </div>
      </ActionsContainer>
    );
  }

  const isKeepDecision = data.decision === 'keep';
  const isChangeDecision = data.decision === 'change';
  const isUnlinkDecision = data.decision === 'unlink';
  const isRejectDecision = isChangeDecision || isUnlinkDecision;
  const isDone = data.status === ConceptLinkerValidatorItemStatus.DONE;

  // Check if AI suggested an alternative
  const hasAlternative = data.ai_result && data.ai_result.selected_ref && data.ai_result.selected_refId;
  const rejectButtonLabel = hasAlternative ? 'Change' : 'Unlink';

  // Button selection states
  const keepSelected = isKeepDecision && isDone;
  const rejectSelected = isRejectDecision && isDone;
  const hasSelection = isDone;

  const handleKeep = async () => {
    if (processingAction || keepSelected) return;
    setProcessingAction('keep');
    try {
      await itemsModel.keepItem(data.id);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleReject = async () => {
    if (processingAction || rejectSelected) return;
    setProcessingAction('reject');
    try {
      if (hasAlternative) {
        await itemsModel.changeItem(data.id);
      } else {
        await itemsModel.unlinkItem(data.id);
      }
    } finally {
      setProcessingAction(null);
    }
  };

  return (
    <ActionsContainer>
      <ActionButton
        onClick={handleKeep}
        disabled={processingAction !== null}
        $selected={keepSelected}
        $hasSelection={hasSelection}
        $variant="keep"
        data-selected={keepSelected ? 'true' : 'false'}
      >
        {processingAction === 'keep' ? <i className="fa fa-spinner fa-spin" /> : 'Keep'}
      </ActionButton>
      <ActionButton
        onClick={handleReject}
        disabled={processingAction !== null}
        $selected={rejectSelected}
        $hasSelection={hasSelection}
        $variant={hasAlternative ? 'change' : 'unlink'}
        data-selected={rejectSelected ? 'true' : 'false'}
      >
        {processingAction === 'reject' ? <i className="fa fa-spinner fa-spin" /> : rejectButtonLabel}
      </ActionButton>
    </ActionsContainer>
  );
};

const ActionsContainer = styled.div`
  display: flex;
  gap: 5px;
  justify-content: center;
  align-items: flex-start;
`;

const ActionButton = styled.button<{
  $selected?: boolean;
  $variant?: 'keep' | 'change' | 'unlink';
  $hasSelection?: boolean;
}>`
  padding: 6px 14px;
  background-color: ${(props) => {
    if (props.$selected) {
      switch (props.$variant) {
        case 'keep':
          return '#66BB6A';
        case 'change':
          return '#42A5F5';
        case 'unlink':
          return '#EF5350';
        default:
          return '#666';
      }
    }
    switch (props.$variant) {
      case 'keep':
        return '#e8f5e9';
      case 'change':
        return '#e3f2fd';
      case 'unlink':
        return '#ffebee';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${(props) => {
    if (props.$selected) return 'white';
    switch (props.$variant) {
      case 'keep':
        return '#2e7d32';
      case 'change':
        return '#1565c0';
      case 'unlink':
        return '#c62828';
      default:
        return '#333';
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$variant) {
        case 'keep':
          return props.$selected ? '#66BB6A' : '#81c784';
        case 'change':
          return props.$selected ? '#42A5F5' : '#64b5f6';
        case 'unlink':
          return props.$selected ? '#EF5350' : '#ef5350';
        default:
          return '#ddd';
      }
    }};
  border-radius: 6px;
  cursor: ${(props) => (props.$selected ? 'default' : 'pointer')};
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  opacity: ${(props) => {
    if (props.$selected) return '1';
    if (props.$hasSelection) return '0.3';
    return '1';
  }};
  min-width: 70px;
  text-align: center;
  pointer-events: ${(props) => (props.$selected ? 'none' : 'auto')};
  box-shadow: ${(props) => (props.$selected ? '0 2px 4px rgba(0,0,0,0.1)' : 'none')};

  &:hover:not(:disabled):not([data-selected='true']) {
    opacity: 1;
    background-color: ${(props) => {
      switch (props.$variant) {
        case 'keep':
          return '#66BB6A';
        case 'change':
          return '#42A5F5';
        case 'unlink':
          return '#EF5350';
        default:
          return '#666';
      }
    }};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

export default ConceptValidatorActionRenderer;
